<template>
  <div class="heatmap-chart" :class="{ 'is-hovered': isHoverActive, 'is-rendering': width === 0 }">
    <svg ref="svg" :width="width + margin.left + margin.right" :height="height + margin.top + margin.bottom">
      <rect class="hover-overlay" :class="{ 'is-active': isHoverActive }" :width="x(1)" :height="height" :x="x(hoverX)" :style="{ 'transform': `translate(${margin.left}px, ${margin.top}px)` }" />

      <g :style="{ 'transform': `translate(${margin.left}px, ${margin.top}px)` }">
        <rect class="grid-item" v-for="(item, index) in data" :key="index" :class="{ 'is-active': hoverX === item.hour }" :width="x(1)" :x="x(item.hour)" :y="y(item.type)" :fill="getFill(item.value)" />
      </g>

      <g class="x axis" ref="xAxis" :style="{ 'transform': `translate(${margin.left}px, ${height + margin.top + 14}px)` }"></g>

      <g class="y axis">
        <text v-for="(item, index) in y.domain()" :key="index" :y="y(item) + 20">{{ item }}</text>
      </g>

      <line class="chart-divider" :x1="0" :y1="height + margin.top" :x2="width" :y2="height + margin.top" :style="{ 'transform': `translate(${margin.left}px, 0px)` }" />

      <rect ref="overlay" class="overlay" :width="width" :height="height" :style="{ 'transform': `translate(${margin.left}px, ${margin.top}px)` }" />

      <g class="selection" :class="{ 'is-active': isSelectionActive, 'is-expanding': isSelectionExpanding } ">
        <rect ref="selectionArea" class="selection-area" :width="x(selectionDomain[1]) - x(selectionDomain[0])" :height="height" ry="1" rx="1" :style="{ 'transform': `translate(${margin.left + x(selectionDomain[0])}px, ${margin.top}px)` }" />
        <rect ref="selectionButtonStart" class="selection-button" ry="2.5" rx="2.5" :style="{ 'transform': `translate(${margin.left + x(selectionDomain[0]) - 2.2}px, ${margin.top + height / 2 - 24 / 2}px)` }" />
        <rect ref="selectionButtonEnd" class="selection-button" ry="2.5" rx="2.5" :style="{ 'transform': `translate(${margin.left + x(selectionDomain[1]) - 2.2}px, ${margin.top + height / 2 - 24 / 2}px)` }" />
      </g>
    </svg>

    <div class="time-range" :class="{ 'is-active': isInteractionActive }" :style="{ 'transform': `translate(${margin.left + timeRangeOffset}px, ${height + margin.top + 14}px)` }">
      <span class="tag is-black">{{ timeRangeText }}</span>
    </div>

    <div class="tooltip-container" :style="{ 'width': `${width}px`, 'height': `${height}px`, 'transform': `translate(${margin.left}px, ${margin.top}px)` }">
      <div class="tooltip tooltip-right" role="tooltip" ref="tooltip" :class="{ 'is-active': isInteractionActive }" :style="{ 'transform': `translate(${tooltipOffset + 18}px, -50%)` }">
        <div class="tooltip-arrow"></div>
        <div class="tooltip-inner">
          <div v-for="(item, index) in tooltipContent" :key="index" class="level">
            <div class="level-left">
              <div class="level-item item-type">
                <i class="material-icons" v-if="icons[item.type]">{{ icons[item.type] }}</i>
                {{ item.type }}
              </div>
            </div>
            <div class="level-right">
              <div class="level-item">{{ $t(item.value) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { select, mouse, event } from 'd3-selection';
import { scaleLinear, scaleBand } from 'd3-scale';
import { axisBottom } from 'd3-axis';
import { range } from 'd3-array';
import { interpolateHcl } from 'd3-interpolate';
import { rgb } from 'd3-color';
import { drag } from 'd3-drag';
import { sortedUniqBy, map, flow } from 'lodash/fp';
import { setHours, format } from 'date-fns';
import icons from '@/utils/icons';
import { resizable } from 'views/mixins'

export default {

  mixins: [resizable],

  props: {
    data: {
      type: Array,
      required: true
    },
    tooltipContent: Array
  },

  data() {
    return {
      dataByTime: [],
      width: 0,
      height: 0,
      margin: { top: 12, right: 3, bottom: 36, left: 72 },
      x: scaleLinear(),
      y: scaleBand(),
      xAxis: axisBottom(),
      color: scaleLinear(),
      hoverX: null,
      selectionStartX: null,
      selectionEndX: null,
      repositionX: null,
      repositionStartX: null,
      repositionCurrentX: null,
      isSelectionActive: false,
      isSelectionExpanding: false,
      isSelectionStarted: false,
      activeSelectionButton: null,
      icons: icons
    };
  },

  computed: {
    selectionDomain() {
      if (this.selectionStartX > this.selectionEndX) {
        return [this.selectionEndX, this.selectionStartX + 1];
      }

      return [this.selectionStartX, this.selectionEndX + 1];
    },

    timeRangeText() {
      if (this.isSelectionActive) {
        return `${format(setHours(new Date(0), this.selectionDomain[0]), 'h a')} - ${format(setHours(new Date(0), this.selectionDomain[1]), 'h a')}`;
      }

      return `${format(setHours(new Date(0), this.hoverX), 'h a')} - ${format(setHours(new Date(0), this.hoverX + 1), 'h a')}`;
    },

    timeRangeOffset() {
      if (this.isSelectionActive) {
        return this.x(this.selectionDomain[0] + (this.selectionDomain[1] - this.selectionDomain[0]) / 2);
      }

      return this.x(this.hoverX) + this.x(1) / 2;
    },

    tooltipOffset() {
      if (this.isSelectionActive) {
        return this.x(this.selectionDomain[1]);
      }

      return this.x(this.hoverX + 1);
    },

    isHoverActive() {
      if (this.hoverX !== null) {
        return true;
      }

      return false;
    },

    isInteractionActive() {
      if (this.hoverX !== null || this.isSelectionActive) {
        return true;
      }

      return false;
    }
  },

  mounted() {
    this.width = this.$el.getBoundingClientRect().width - this.margin.left - this.margin.right;
    this.height = this.$el.getBoundingClientRect().height - this.margin.top - this.margin.bottom;

    if (this.width < 0) {
      this.width = 0;
    }

    this.x.range([0, this.width])
      .domain([0, 24]);

    this.y.range([0, this.height])
      .domain(this.getYDomain(this.data));

    this.color.interpolate(interpolateHcl)
      .range([rgb('#aadcf4'), rgb('#000249')])
      .domain([0, 1]);

    const [rangeStart, rangeStop] = this.x.domain();

    this.xAxis.scale(this.x)
      .tickValues([...range(rangeStart, rangeStop, rangeStop / 8), rangeStop])
      .tickFormat(d => format(setHours(new Date(0), d), d % (rangeStop / 2) === 0 ? 'h a' : 'h'));

    select(this.$refs.xAxis)
      .call(this.xAxis);

    select(this.$refs.xAxis).select('.domain')
      .remove();

    select(this.$refs.overlay).on('mouseover', this.mouseover)
      .on('mouseout', this.mouseout)
      .on('mousemove', this.mousemove);

    const dragBehaviorCreate = drag().container(this.$refs.overlay)
      .on('start', this.createSelectionStart)
      .on('end', this.createSelectionEnd)
      .on('drag', this.createSelectionMove);

    const dragBehaviorExpand = drag().container(this.$refs.overlay)
      .on('start', this.expandSelectionStart)
      .on('end', this.expandSelectionEnd)
      .on('drag', this.expandSelectionMove);

    const dragBehaviorReposition = drag().container(this.$refs.overlay)
      .on('start', this.repositionSelectionStart)
      .on('end', this.repositionSelectionEnd)
      .on('drag', this.repositionSelectionMove);

    select(this.$refs.overlay).call(dragBehaviorCreate);
    select(this.$refs.selectionButtonStart).call(dragBehaviorExpand);
    select(this.$refs.selectionButtonEnd).call(dragBehaviorExpand);
    select(this.$refs.selectionArea).call(dragBehaviorReposition);

    select('body').on('click', () => {
      if (event.toElement !== this.$refs.selectionArea && event.toElement !== this.$refs.selectionButtonStart && event.toElement !== this.$refs.selectionButtonEnd) {
        this.isSelectionActive = false;
      }
    });
  },

  methods: {
    onResize() {
      this.width = 0;

      this.$nextTick(() => {
        this.width = this.$el.getBoundingClientRect().width - this.margin.left - this.margin.right;
        this.x.range([0, this.width]);
        select(this.$refs.xAxis).call(this.xAxis);
        
        select(this.$refs.xAxis).select('.domain')
          .remove();
      });
    },

    getYDomain(data) {
      return flow(
        sortedUniqBy('type'),
        map(item => item.type)
      )(data);
    },

    getFill(value) {
      return value ? this.color(value) : '#eaebed';
    },

    mouseover() {
      if (this.isSelectionActive) {
        return;
      }

      this.hoverX = this.getMouseX();
    },

    mouseout() {
      this.hoverX = null;
    },

    mousemove() {
      if (this.isSelectionActive) {
        return;
      }
      
      this.hoverX = this.getMouseX();
    },

    createSelectionStart() {
      this.isSelectionActive = false;
      this.isSelectionStarted = true;
    },

    createSelectionEnd() {
      if (this.selectionStartX > this.selectionEndX) {
        const selectionStartX = this.selectionStartX;
        const selectionEndX = this.selectionEndX;

        this.selectionStartX = selectionEndX;
        this.selectionEndX = selectionStartX;
      }
    },

    createSelectionMove() {
      if (this.isSelectionStarted) {
        this.selectionStartX = this.getMouseX();
        this.isSelectionStarted = false;
      }

      this.isSelectionActive = true;
      this.selectionEndX = this.getMouseX();
    },

    expandSelectionStart() {
      this.activeSelectionButton = event.sourceEvent.target;
      this.isSelectionExpanding = true;
    },

    expandSelectionEnd() {
      this.isSelectionExpanding = false;
    },

    expandSelectionMove() {
      const mouseX = this.getMouseX();

      if (this.activeSelectionButton === this.$refs.selectionButtonStart && mouseX <= this.selectionEndX) {
        this.selectionStartX = this.getMouseX();
      }

      if (this.activeSelectionButton === this.$refs.selectionButtonEnd && mouseX >= this.selectionStartX) {
        this.selectionEndX = this.getMouseX();
      }
    },

    repositionSelectionStart() {
      this.repositionX = this.getMouseX();
    },

    repositionSelectionEnd() {
    },

    repositionSelectionMove() {
      if (this.repositionX === this.getMouseX()) {
        return;
      }
      
      if (this.getMouseX() > this.repositionX && this.selectionEndX + 1 < this.x.domain()[1]) {
        this.selectionStartX = this.selectionStartX + 1;
        this.selectionEndX = this.selectionEndX + 1;
      }

      if (this.getMouseX() < this.repositionX && this.selectionStartX > this.x.domain()[0]) {
        this.selectionStartX = this.selectionStartX - 1;
        this.selectionEndX = this.selectionEndX - 1;
      }

      this.repositionX = this.getMouseX();
    },

    getMouseX() {
      const position = mouse(this.$refs.overlay);
      const x = Math.floor(this.x.invert(position[0]));

      if (x >= this.x.domain()[1]) {
        return this.x.domain()[1] - 1;
      }

      if (x <= this.x.domain()[0]) {
        return this.x.domain()[0];
      }

      return x;
    }
  },

  watch: {
    data: function (newData) {
      this.data = newData;
      this.y.domain(this.getYDomain(this.data));
    },

    selectionDomain: function (newValue) {
      if (this.isSelectionActive) {
        this.$emit('interaction', newValue);
      }
    },

    hoverX: function (newValue) {
      if (newValue !== null) {
        this.$emit('interaction', [newValue, newValue + 1]);
      }
    }
  }
};
</script>

<style lang="scss">
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.heatmap-chart {
  position: relative;

  &.is-hovered {
    .grid-item {
      &:not(.is-active) {
        opacity: .32;
      }
    }
  }

  &.is-rendering {
    .axis {
      &.x {
        display: none;
      }
    }
  }

  .hover-overlay {
    fill: #f2f2f2;
    pointer-events: none;
    opacity: 0;

    &.is-active {
      opacity: 1;
      transition: opacity .2s $timing-function;
    }
  }

  .grid-item {
    height: 10px;
    stroke: $white;
    stroke-width: .5px;
    transition: opacity .2s $timing-function;

    &.is-active {
      stroke-width: 0;
    }
  }

  .selection {
    opacity: 0;
    pointer-events: none;
    transition: opacity .15s $timing-function;

    &.is-active {
      opacity: 1;
      pointer-events: initial;

      .selection-button {
        cursor: grab;
      }
    }

    &.is-expanding {
      .selection-area,
      .selection-button {
        cursor: -webkit-grabbing;
      }
    }
  }

  .selection-area {
    fill: rgba(33, 150, 243, 0.04);
    stroke: #2196f3;
  }

  .selection-button {
    fill: $white;
    stroke: #2196f3;
    height: 24px;
    width: 4.4px;
  }

  .axis {
    text {
      font-family: $family-sans-serif;
      font-size: $size-small;
      opacity: .64;
    }

    &.x {
      .domain {
        display: none;
      }

      .tick {
        &:first-child {
          text-anchor: start;
        }

        &:last-child {
          text-anchor: end;
        }

        line {
          display: none;
        }
      }

      text {
        fill: $black-bis;
      }
    }

    &.y {
      text {
        fill: $black;
        text-transform: capitalize;
        text-anchor: start;
      }
    }
  }

  .chart-divider {
    stroke: $grey-darker;
    stroke-width: 1px;
    opacity: .2;
  }

  .overlay {
    fill: none;
    pointer-events: all;
  }

  .time-range {
    position: absolute;
    opacity: 0;

    &.is-active {
      transition: opacity .2s $timing-function;
      opacity: 1;
    }

    .tag {
      background-color: $black-bis;
      color: $white;
      border-radius: 2px;
      height: 1.25rem;
      line-height: 1.33;
      padding: .125rem .5rem;
      transform: translateX(-50%);
    }
  }

  .tooltip-container {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
  }

  .tooltip {
    transition: none;
    top: 50%;
    bottom: auto;
    left: auto;
    opacity: 0;

    &.is-active {
      opacity: 1;
      transition: opacity .2s $timing-function;
    }

    .level {
      margin-bottom: .75rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .level-left {
      & + .level-right {
        margin-left: 1.75rem;
      }
    }

    .item-type {
      text-transform: capitalize;

      i {
        font-size: $size-small;
        margin-right: .625rem;
      }
    }
  }
}
</style>

